import type { FavouriteProduct } from '@/scopes/favourites/domain/favouriteTypes'
import type { FavoritePageLoadStatus, FavoritePageRemovalStatus } from '@/scopes/favourites/domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Layout } from '@sunwebgroup/uds-ui/layout'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useCMSPageField } from '@/cms/fields'
import { useUser } from '@/domain/user/hooks'
import { Footer } from '@/scopes/common/components'
import { CatalogueContent } from '@/scopes/common/components/Footer/CatalogueContent'
import { Header } from '@/scopes/common/components/Header'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { useLocation } from '@/scopes/common/infrastructure/dom/location'
import { createFavoritePageActions } from '@/scopes/favourites/domain/actions'
import { addReturnUrl } from '@/scopes/favourites/domain/addReturnUrl'
import { createFavoriteImpressionViewEvent, createFavoriteRemovalEvent } from '@/scopes/favourites/domain/analytics'
import { favouritesAPIClient } from '@/scopes/favourites/infrastructure/api'
import { FavoritePageDependenciesProvider, useFavoritePageActions } from '@/scopes/favourites/infrastructure/dependencies'
import { favoritePageStore } from '@/scopes/favourites/infrastructure/store'
import { useFavoritesSelector } from '@/scopes/favourites/infrastructure/useFavoritesSelector'
import { FavoriteCard } from './FavoriteCard'
import { FavoriteHero } from './FavoriteHero'
import styles from './FavoritePage.module.css'
import { favoritePageSelector } from './selectors'

const queryClient = new QueryClient()
function FavoritePageContent() {
  const intl = useIntl()
  const [isFavoritePageInitialized, setIsFavoritePageInitialized] = React.useState(false)
  const [returnUrl, setReturnUrl] = React.useState('')
  const { isLoading: isUserLoading, isLoggedIn } = useUser()
  const contextItemId = useCMSPageField('contextItemId')
  const headerImageUrl = useCMSPageField('FavoritePage.header.imageUrl')
  const links = useConfiguration('links')
  const logger = useCommonDependency('logger')
  const location = useLocation()
  const {
    allFavouritesRemovalStatus,
    loadStatus,
    results,
    removingProductIds,
  } = useFavoritesSelector(favoritePageSelector)
  const loggerRef = React.useRef(logger)
  loggerRef.current = logger
  const actions = React.useMemo(() => createFavoritePageActions({
    apiClient: favouritesAPIClient,
    invalidateFavouriteProducts: () => queryClient.invalidateQueries({ queryKey: ['favourite-products'] }),
    logger: { logError: (error, info) => loggerRef.current.logError(error, info) },
    store: favoritePageStore,
  }), [])

  React.useEffect(() => {
    if (!isLoggedIn) {
      setIsFavoritePageInitialized(false)
      return
    }

    setIsFavoritePageInitialized(true)
    actions.pageLoaded(contextItemId)
  }, [actions, contextItemId, isLoggedIn])

  React.useEffect(() => {
    setReturnUrl(encodeURIComponent(location.pathQueryFragment()))
  }, [location])

  const loginHref = addReturnUrl(links.login, returnUrl)
  const createAccountHref = addReturnUrl(links.createAccount, returnUrl)
  const favoritePageDependencies = React.useMemo(() => ({ actions }), [actions])

  return (
    <FavoritePageDependenciesProvider dependencies={favoritePageDependencies}>
      <Header />
      <main className={styles.page}>
        <Layout>
          <FavoriteHero
            imageUrl={headerImageUrl}
            title={intl.formatMessage({ id: 'favorite_page_title' })}
          />
          <div className={styles.content}>
            {isUserLoading && <PageLoading />}
            {!isUserLoading && !isLoggedIn && (
              <section className={styles.loggedOutState} aria-live="polite">
                <Typography as="h2" variant="display-g-regular">
                  {intl.formatMessage({ id: 'favorite_page_logged_out_title' })}
                </Typography>
                <Typography as="p" variant="body-m-regular-snug">
                  {intl.formatMessage({ id: 'favorite_page_logged_out_description' })}
                </Typography>
                <Button as="a" className={styles.loginButton} href={loginHref} variant="primary">
                  {intl.formatMessage({ id: 'favorite_page_login_action' })}
                </Button>
                <div className={styles.divider} />
                <div className={styles.accountActions}>
                  <Typography as="span" variant="body-m-regular-snug">
                    {intl.formatMessage({ id: 'favorite_page_no_account_label' })}
                  </Typography>
                  <Button as="a" className={styles.createAccountButton} href={createAccountHref} variant="transparentNeutral">
                    {intl.formatMessage({ id: 'favorite_page_create_account_action' })}
                  </Button>
                </div>
              </section>
            )}
            {!isUserLoading && isLoggedIn && (
              <AuthenticatedFavoriteContent
                allFavouritesRemovalStatus={allFavouritesRemovalStatus}
                isFavoritePageInitialized={isFavoritePageInitialized}
                loadStatus={loadStatus}
                removingProductIds={removingProductIds}
                results={results}
                searchHref={links.search}
              />
            )}
          </div>
        </Layout>
      </main>
      <Footer className={styles.footer}>
        <CatalogueContent />
      </Footer>
    </FavoritePageDependenciesProvider>
  )
}

interface AuthenticatedFavoriteContentProps {
  allFavouritesRemovalStatus: FavoritePageRemovalStatus
  isFavoritePageInitialized: boolean
  loadStatus: FavoritePageLoadStatus
  results: FavouriteProduct[]
  removingProductIds: number[]
  searchHref: string
}

function AuthenticatedFavoriteContent({
  allFavouritesRemovalStatus,
  isFavoritePageInitialized,
  loadStatus,
  results,
  removingProductIds,
  searchHref,
}: AuthenticatedFavoriteContentProps) {
  const intl = useIntl()
  const contextItemId = useCMSPageField('contextItemId')
  const { sendEvent } = useAnalytics()
  const {
    allFavouritesRemovalRequested,
    favouriteRemovalRequested,
    favouritesRetryRequested,
  } = useFavoritePageActions()
  const hasReportedImpressions = React.useRef(false)

  React.useEffect(() => {
    if (!isFavoritePageInitialized || loadStatus !== 'success' || hasReportedImpressions.current) {
      return
    }

    sendEvent(createFavoriteImpressionViewEvent(results))
    hasReportedImpressions.current = true
  }, [isFavoritePageInitialized, loadStatus, results, sendEvent])

  if (!isFavoritePageInitialized || loadStatus === 'idle' || loadStatus === 'loading') {
    return <PageLoading />
  }

  if (loadStatus === 'error') {
    return (
      <section className={styles.state} aria-live="polite">
        <Typography as="p" variant="body-l-regular-snug">
          {intl.formatMessage({ id: 'favorite_page_load_error_description' })}
        </Typography>
        <Button onClick={() => favouritesRetryRequested(contextItemId)} variant="primary">
          {intl.formatMessage({ id: 'favorite_page_retry_action' })}
        </Button>
      </section>
    )
  }

  if (results.length === 0) {
    return (
      <section className={styles.emptyState}>
        <Typography as="h2" variant="display-g-regular">
          {intl.formatMessage({ id: 'favorite_page_empty_title' })}
        </Typography>
        <Typography as="p" variant="body-m-regular-snug">
          {intl.formatMessage({ id: 'favorite_page_empty_description' })}
        </Typography>
        <Button as="a" className={styles.emptyStateAction} href={searchHref} variant="primary">
          <Button.Icon glyph="Magnifier" />
          {intl.formatMessage({ id: 'favorite_page_empty_action' })}
        </Button>
      </section>
    )
  }

  function handleFavoriteRemoval(product: FavouriteProduct) {
    sendEvent(createFavoriteRemovalEvent(product))
    favouriteRemovalRequested(contextItemId, product.id)
  }

  return (
    <>
      <section className={styles.results} aria-live="polite">
        {results.map(product => (
          <FavoriteCard
            isRemoving={removingProductIds.includes(product.id)}
            key={product.id}
            onRemove={() => handleFavoriteRemoval(product)}
            product={product}
          />
        ))}
      </section>
      {allFavouritesRemovalStatus === 'error' && (
        <Typography as="p" role="alert" variant="body-m-regular-snug">
          {intl.formatMessage({ id: 'favorite_page_remove_all_error_description' })}
        </Typography>
      )}
      <Button
        className={styles.removeAllButton}
        disabled={allFavouritesRemovalStatus === 'loading'}
        onClick={allFavouritesRemovalRequested}
        variant="outlinedPrimary"
        fullWidth={false}
      >
        <Button.Icon glyph="TrashCan" />
        {intl.formatMessage({ id: 'favorite_page_remove_all_action' })}
      </Button>
    </>
  )
}

function PageLoading() {
  const intl = useIntl()

  return (
    <div aria-label={intl.formatMessage({ id: 'favorite_page_loading_label' })} className={styles.loading} role="status">
      <IndeterminateProgressCircle />
    </div>
  )
}

export function FavoritePage() {
  return (
    <QueryClientProvider client={queryClient}>
      <FavoritePageContent />
    </QueryClientProvider>
  )
}
